import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn, CheckCircle2, AlertCircle, Mail, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";


const ERROR_MESSAGES = {
  no_account: "We couldn't find an HCASP account for that email. Please create one first.",
  sso_denied: "Sign-in was cancelled.",
  sso_failed: "Something went wrong during sign-in. Please try again.",
  sso_invalid_state: "Your sign-in session expired. Please try again.",
  sso_missing_code: "Something went wrong during sign-in. Please try again.",
  sso_no_email: "Your identity provider account has no email on file. Please contact support.",
  sso_unavailable: "Sign-in is temporarily unavailable. Please try again shortly.",
};

const Login = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, user } = useAuth();

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const confirmed = params.get("confirmed");
  const errorCode = params.get("error");

  useEffect(() => {
    if (user) navigate("/user-panel");
  }, [user, navigate]);


const handleLogin = async (e) => {
  e.preventDefault();

  // Prevent multiple submissions
  if (submitting) return;

  const trimmedEmail = email.trim().toLowerCase();

  // Validate before API call
  if (!trimmedEmail || !password) {
    toast.error("Please enter both email and password.");
    return;
  }

  setSubmitting(true);

  try {
    await login({
      email: trimmedEmail,
      password,
    });

    toast.success("Signed in successfully.");
    navigate("/user-panel");
  } catch (err) {
    toast.error(formatApiError(err) || "Invalid email or password.");
  } finally {
    setSubmitting(false);
  }
};



  return (
    <div className="min-h-screen bg-[#07070c] text-white font-body">
      <Nav />
      <div className="relative min-h-screen flex items-center justify-center px-6 noise overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full blur-[130px] opacity-40 bg-[#4d4479]" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="login-card"
          className="relative z-10 w-full max-w-md rounded-2xl border border-[#3d414f] bg-[#0a0a12]/90 backdrop-blur-xl p-8 md:p-10"
        >
          {/* <div className="flex items-center gap-3 mb-2">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#16fcfb] text-[#07070c]"><LogIn size={18} onClick={() => navigate("/login")} /></span>
            <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#16fcfb]">Member Sign In</span>
          </div> */}
          <h1 className="font-display font-700 text-3xl text-white">Welcome</h1>
          <p className="mt-2 font-body text-sm text-white/60">Sign in to access your hackTonomy dashboard.</p>

          {confirmed === "1" && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-[#16fcfb]/40 bg-[#16fcfb]/10 p-4" data-testid="confirmed-banner">
              <CheckCircle2 size={18} className="text-[#16fcfb] mt-0.5 shrink-0" />
              <p className="font-body text-sm text-white/85">Your email is confirmed. You can now sign in.</p>
            </div>
          )}
          {confirmed === "0" && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-[#4d4479] bg-[#4d4479]/20 p-4">
              <AlertCircle size={18} className="text-[#97b8ff] mt-0.5 shrink-0" />
              <p className="font-body text-sm text-white/85">
                Please confirm your email before signing in — or this confirmation link is invalid or expired.
              </p>
            </div>
          )}
          {errorCode && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-[#4d4479] bg-[#4d4479]/20 p-4" data-testid="sso-error-banner">
              <AlertCircle size={18} className="text-[#97b8ff] mt-0.5 shrink-0" />
              <p className="font-body text-sm text-white/85">
                {ERROR_MESSAGES[errorCode] || "Something went wrong during sign-in. Please try again."}
              </p>
            </div>
          )}


          <form onSubmit={handleLogin} className="mt-7 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                Email
              </Label>
              <Input
                id="login-email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="login-password" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="login-password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              className="w-full rounded-full h-12 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan"
            >
              {submitting ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Signing in</>) : "Sign In"}
            </Button>

            <p className="text-center">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-[#16fcfb] hover:text-[#97b8ff]">
                Forgot your password?
              </Link>
            </p>
          </form>


          <p className="mt-6 text-center font-body text-sm text-white/55">
            Don&apos;t have an account?{" "}
            <Link to="/sign-up" className="text-[#16fcfb] hover:underline">Sign Up</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;